import type { UciEngine } from './uci-engine.js';

interface Pending {
  match: (line: string) => boolean;
  resolve: (lines: string[]) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  /** Optional per-line tap — used by the api to stream SSE while analysing. */
  onLine?: (line: string) => void;
}

/**
 * Sequential UCI client: UCI is strictly one-command-one-response, so at most
 * one `ask` is in flight. `post` is fire-and-forget for setoption/position.
 */
export class UciClient {
  private pending: Pending | null = null;
  private lines: string[] = [];

  constructor(private readonly engine: UciEngine) {
    engine.onMessage((line) => this.onLine(line));
  }

  post(line: string): void {
    this.engine.postMessage(line);
  }

  ask(line: string, match: (l: string) => boolean, timeoutMs = 8000, onLine?: (l: string) => void): Promise<string[]> {
    return new Promise((resolve, reject) => {
      this.lines = [];
      const pending: Pending = {
        match,
        resolve,
        reject,
        timer: 0 as unknown as ReturnType<typeof setTimeout>,
        onLine,
      };
      pending.timer = setTimeout(() => {
        if (this.pending !== pending) return; // stale timer from a superseded ask
        this.pending = null;
        pending.reject(new Error(`UCI timeout: ${line}`));
      }, timeoutMs);
      this.pending = pending;
      this.engine.postMessage(line);
    });
  }

  private onLine(line: string): void {
    if (line.startsWith('error')) return; // engine-side error; caller hits timeout
    this.lines.push(line);
    this.pending?.onLine?.(line);
    const p = this.pending;
    if (p === null) return;
    if (p.match(line)) {
      clearTimeout(p.timer);
      this.pending = null;
      p.resolve([...this.lines]);
    }
  }
}
